import { CreateMessageData, ResponseMessages, ResponseMessagesWithMeta } from "@sms/shared/message/message.interface";
import { CurrentChatData } from "@sms/shared/chat/chat.interface";
import { Action } from "redux";
import { EventChannel, Task } from "redux-saga";
import { Effect, select, put, call, take, fork, cancel, cancelled, all, takeEvery } from "redux-saga/effects";
import { PayloadAction } from "typesafe-actions";
import { MessageRepeatedCountData } from "@sms/shared/message/message-repeated-count.interface";
import * as Sentry from "@sentry/react";

import { ApplicationStore } from "../application.store";
import chatActions from "../chat/chat.actions";

import {
  connectedSocketMessage,
  createMessageAction,
  getMessagesAction,
  getMessagesNextPageAction,
  handleUpdateMessage,
  reconnectedSocketMessage,
  setInitMessages,
  setInitMetaMessages,
  setMessages,
  setMessagesRepeated,
  setMetaMessages,
  setUpdateMessages,
  updateMessageStatusToReadAction
} from "./messages.actions";
import { connectSocketMessage, createSocketEventsMessagesChannel, socketMessages } from "./messages.socket";
import messagesService from "./messages.service";

function* initMessages(): Generator {
  yield fork(getMessagesSaga);
  yield fork(handleGetMessagesRepeated);

  yield fork(fetchMessages);
}

function* fetchMessages(): Generator {
  const selectChatId: string | null = (yield select((state: ApplicationStore) => state.chats.selectChatId)) as
    | string
    | null;

  if (selectChatId !== null) {
    try {
      console.log("Joining chat room:", selectChatId);
      socketMessages.emit("joinChatRoom", { chatId: selectChatId });

      const responseMessages: ResponseMessagesWithMeta = yield call(messagesService.fetchMessages, selectChatId);

      console.log("Fetched messages");

      if (responseMessages) {
        yield put(setInitMessages(responseMessages.data));
        yield put(setInitMetaMessages(responseMessages.meta));
      }
    } catch (e) {
      if (e instanceof Error) {
        console.error(e);
      }
    }
  }
}

function* fetchMessagesNextPage(): Generator {
  const selectChatId: string | null = (yield select((state: ApplicationStore) => state.chats.selectChatId)) as
    | string
    | null;

  const page: number | undefined = (yield select((state: ApplicationStore) => state.message.meta?.page)) as
    | number
    | undefined;

  const messages: ResponseMessages[] | null = (yield select((state: ApplicationStore) => state.message.messages)) as
    | ResponseMessages[]
    | null;

  const countMessages: number | undefined = messages?.length;

  if (selectChatId !== null) {
    try {
      const responseMessagesNextPage: ResponseMessagesWithMeta = yield call(messagesService.fetchMessagesNextPage, {
        chatId: selectChatId,
        page,
        countMessages
      });

      if (responseMessagesNextPage) {
        yield put(setMessages(responseMessagesNextPage.data));
        yield put(setMetaMessages(responseMessagesNextPage.meta));
      }
    } catch (e) {
      if (e instanceof Error) {
        console.error(e);
      }
    }
  }
}

function getMessagesRepeated(): Promise<MessageRepeatedCountData[]> {
  return new Promise((resolve, reject) => {
    socketMessages.emit("fetchMessagesRepeated", (data: MessageRepeatedCountData[]) => {
      if (data) {
        resolve(data);
      } else {
        reject(new Error("Failed to fetch messages repeated list."));
      }
    });
  });
}

function* handleGetMessagesRepeated(): Generator<Effect | Generator, void, MessageRepeatedCountData[]> {
  try {
    const messagesRepeated: MessageRepeatedCountData[] = yield call(getMessagesRepeated);
    yield put(setMessagesRepeated(messagesRepeated));
  } catch (e) {
    console.error(e);
  }
}

function createMessage(message: CreateMessageData): void {
  message.images.map(image => {
    if (image.file) {
      image.name = image.file.name;
      image.size = image.file.size;
      image.lastModified = image.file.lastModified;
      image.dataURL = "";
    }
  });

  try {
    messagesService.sendMessage(message);
  } catch (e) {
    if (e instanceof Error) {
      console.error(e.message);
      Sentry.captureException(e.message);
    }
  }
}

export function* getMessagesSaga(): Generator<Effect | Generator, Task | CurrentChatData> {
  while (true) {
    yield take(getMessagesAction);
    console.log("Starting the message retrieval function");
    const fetchMessagesTask: Task = (yield fork(fetchMessages)) as Task;

    const selectChatId: string | null = (yield select((state: ApplicationStore) => state.chats.selectChatId)) as
      | string
      | null;

    yield take(chatActions.switchSelectChat);
    console.log("Switching chat room");
    socketMessages.emit("leaveChatRoom", { chatId: selectChatId });
    console.log("End of the message retrieval function");
    yield cancel(fetchMessagesTask);
  }
}

export function* updateMessageSaga(data: ResponseMessages): Generator {
  const messagesStore: ResponseMessages[] = (yield select(
    (store: ApplicationStore) => store.message.messages
  )) as ResponseMessages[];

  // if (currentChat?.id === data.chatId) {
  const messages: ResponseMessages[] = [...(messagesStore ?? [])] as ResponseMessages[];

  const messageIndex: number = messages.findIndex(message => message.id === data.id);

  if (messageIndex === -1) {
    messages.push(data);
  } else {
    messages[messageIndex] = data;
  }

  yield put(setUpdateMessages(messages));
  // }
}

export function handleMessageStatusReadUpdateSaga(messageId: string): void {
  try {
    socketMessages.emit("handleUpdateMessageStatusToRead", messageId);
  } catch (e) {
    if (e instanceof Error) {
      console.error(e);
    }
  }
}

function* connectSocketMessageSaga(): Generator {
  let task: Task | null = null;

  try {
    yield call(connectSocketMessage);

    task = yield fork(socketWatchers);
  } finally {
    if (yield cancelled()) {
      socketMessages.close();
      if (task !== null) {
        yield cancel(task);
      }
    }
  }
}

export function* socketWatchers(): Generator<Effect | Generator, void, EventChannel<any> | Action<any>> {
  const socketEventsMessagesChannel: EventChannel<any> = (yield call(
    createSocketEventsMessagesChannel
  )) as EventChannel<any>;

  while (true) {
    const action: Action<any> = (yield take(socketEventsMessagesChannel)) as Action<any>;

    yield put(action);
  }
}

export function* messagesWatchers(): Generator<Effect | Generator, void, void> {
  yield all([
    takeEvery(getMessagesNextPageAction, () => fetchMessagesNextPage()),
    takeEvery(createMessageAction, ({ payload }: PayloadAction<string, CreateMessageData>) => createMessage(payload)),
    takeEvery(updateMessageStatusToReadAction, ({ payload }: PayloadAction<string, string>) =>
      handleMessageStatusReadUpdateSaga(payload)
    ),
    takeEvery(handleUpdateMessage, ({ payload }: PayloadAction<string, ResponseMessages>) =>
      updateMessageSaga(payload)
    ),
    takeEvery([connectedSocketMessage, reconnectedSocketMessage], () => initMessages())
  ]);
}

export function* messagesSaga(): Generator<Effect | Generator, void> {
  yield all([fork(messagesWatchers), connectSocketMessageSaga()]);
}
